/**
 * Abstraction types of the Web Geolocation API (with documentation in comparison to the ones provided with node)
 */
export interface GeolocationCoordinates {
    /**
    * a double representing the position's latitude in decimal degrees.
    */
    latitude: number;
    /**
    * A double representing the position's longitude in decimal degrees.
    */
    longitude: number;
    /**
    * A double representing the accuracy of the latitude and longitude properties,
    * expressed in meters.
    */
    accuracy: number;
    /**
    * A double representing the position's altitude in metres, relative to sea
    * level. This value can be null if the implementation cannot provide the data.
    */
    altitude: number;
    /**
    * A double representing the accuracy of the altitude expressed in meters.
    * This value can be null.
    */
    altitudeAccuracy: number;
    /**
    * A double representing the direction in which the device is traveling. This
    * value, specified in degrees, indicates how far off from heading true north
    * the device is. 0 degrees represents true north, and the direction is
    * determined clockwise (which means that east is 90 degrees and west is 270
        * degrees). If speed is 0, heading is NaN. If the device is unable to provide
        * heading information, this value is null.
        */
    heading: number;
    /**
    * A double representing the velocity of the device in meters per second.
    * This value can be null.
    */
    speed: number;
}

export interface GeolocationPosition {
    /**
    * A Coordinates object defining the current location
    */
    coords: GeolocationCoordinates;
    /**
    * A timestamp representing the time at which the location was retrieved.
    */
    timestamp: number;
}

export interface GeolocationOptions {
    /**
    * Is a positive long value indicating the maximum age in milliseconds of a
    * possible cached position that is acceptable to return. If set to 0, it
    * means that the device cannot use a cached position and must attempt to
    * retrieve the real current position. If set to Infinity the device must
    * return a cached position regardless of its age. Default: 0.
    */
    maximumAge?: number;
    /**
    * Is a positive long value representing the maximum length of time
    * (in milliseconds) the device is allowed to take in order to return a
    * position. The default value is Infinity, meaning that getCurrentPosition()
    * won't return until the position is available.
    */
    timeout?: number;
    /**
    * Indicates the application would like to receive the best possible results.
    * If true and if the device is able to provide a more accurate position, it
    * will do so. Note that this can result in slower response times or increased
    * power consumption (with a GPS chip on a mobile device for example). On the
    * other hand, if false, the device can take the liberty to save resources by
    * responding more quickly and/or using less power. Default: false.
    */
    enableHighAccuracy?: boolean;
}

/**
 * The GeolocationPositionError interface represents the reason of an error occurring 
 * when using the geolocating device.
 */
export interface GeolocationPositionError {
    /**
     * The time allowed to acquire the geolocation was reached before the information was obtained.
     */
    readonly code: number;
    /**
     * Returns a human-readable DOMString describing the details of the error. Specifications note that
     *  this is primarily intended for debugging use and not to be shown directly in a user interface.
     */
    readonly message: string;
    /**
     * The acquisition of the geolocation information failed because the page 
     * didn't have the permission to do it.
     */
    readonly PERMISSION_DENIED: number;
    /**
     * The acquisition of the geolocation failed because at least one internal 
     * source of position returned an internal error.
     */
    readonly POSITION_UNAVAILABLE: number;
    /**
     * The time allowed to acquire the geolocation was reached before the information was obtained.
     */
    readonly TIMEOUT: number;
}

/**
 * Validate if the object is of GeolocationPositionError or a valid position
 */
export function isPositionError(object: any): object is GeolocationPositionError {
    return 'code' in object;
}
